import { SITE_URL, SITE_NAME } from "../config/site";
import { COMPANY, SERVICE_AREAS, ALSO_SERVED } from "../data/company";

// ─────────────────────────────────────────────────────────────────────────────
// Structured data
// ─────────────────────────────────────────────────────────────────────────────
// Everything asserted here is published elsewhere on the site — the same phone
// numbers, the same email, the same city, the same service areas.
//
// NOT ASSERTED, and not to be added without the owner confirming:
//   • streetAddress / postalCode — only the locality is public
//   • openingHours / openingHoursSpecification — never stated anywhere
//   • aggregateRating / review — no verified reviews exist (see TESTIMONIALS
//     in data/company.js). Marking up invented reviews is a manual-action
//     risk, not just a taste problem.
//   • foundingDate, numberOfEmployees, priceRange
// Structured data that contradicts, or outruns, the visible page is the single
// easiest way to get a local business listing discounted.

const absoluteUrl = (path = "/") =>
  path.startsWith("http") ? path : `${SITE_URL}${path}`;

const BUSINESS_ID = `${SITE_URL}/#business`;

/**
 * The business itself. Emitted once, on the homepage, and referenced by @id
 * everywhere else so there's a single canonical node rather than a slightly
 * different copy on every page.
 */
export const businessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": BUSINESS_ID,
  name: COMPANY.name,
  description:
    "Renovation contractor based in Pickering, Ontario, working across Durham Region — kitchens, bathrooms, legal basement conversions, flooring, tile, drywall and painting.",
  url: SITE_URL,
  image: absoluteUrl("/og-default.jpg"),
  logo: absoluteUrl("/Logo.png"),
  telephone: COMPANY.phone.primaryE164,
  email: COMPANY.email,
  address: {
    "@type": "PostalAddress",
    addressLocality: COMPANY.address.locality,
    addressRegion: COMPANY.address.region,
    addressCountry: COMPANY.address.country,
  },
  areaServed: [
    ...SERVICE_AREAS.map((area) => ({
      "@type": "City",
      name: `${area.name}, ON`,
    })),
    ...ALSO_SERVED.map((name) => ({ "@type": "City", name: `${name}, ON` })),
  ],
  sameAs: COMPANY.social.map((channel) => channel.href),
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": BUSINESS_ID },
});

/**
 * Breadcrumb trail. Takes the same array the visible <Breadcrumbs> renders,
 * so the two can't drift apart.
 */
export const breadcrumbSchema = (items = []) => {
  if (items.length < 2) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(item.to ? { item: absoluteUrl(item.to) } : {}),
    })),
  };
};

/** A single service, tied to the business node as its provider. */
export const serviceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.name,
  description: service.summary,
  serviceType: service.name,
  url: absoluteUrl(`/services/${service.slug}`),
  provider: { "@id": BUSINESS_ID },
  areaServed: SERVICE_AREAS.map((area) => ({
    "@type": "City",
    name: `${area.name}, ON`,
  })),
});

/**
 * FAQ markup — only ever emitted on pages where the same questions and answers
 * are visible on screen, which is what the guidelines require. Every caller
 * here renders an <FAQ> block with exactly this data.
 */
export const faqSchema = (faqs = []) => {
  if (!faqs.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
};

/** City landing pages: the business, scoped to one place it serves. */
export const localAreaSchema = (location) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `Home renovation services in ${location.name}`,
  description: location.metaDescription,
  url: absoluteUrl(`/${location.slug}`),
  provider: { "@id": BUSINESS_ID },
  areaServed: {
    "@type": location.isRegion ? "AdministrativeArea" : "City",
    name: location.formalName,
  },
});

/** Portfolio entries, as a plain ordered list of work. */
export const projectCollectionSchema = (projects = []) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Renovation projects by Five Lights Reno",
  url: absoluteUrl("/projects"),
  mainEntity: {
    "@type": "ItemList",
    itemListElement: projects.map((project, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: project.title,
    })),
  },
});
